(function (root, factory) {
  'use strict';
  if (typeof module === 'object' && module.exports) {
    module.exports = { createWishlistStore: factory };
    return;
  }
  if (root.UASWishlist) return;
  var config = {};
  try {
    var element = root.document.getElementById('uas-wishlist-config');
    config = element ? JSON.parse(element.textContent) : {};
  } catch (_) { /* A configuration error is exposed through ready. */ }
  root.UASWishlist = factory(root, config);
})(typeof window !== 'undefined' ? window : globalThis, function createWishlistStore(win, suppliedConfig) {
  'use strict';

  var READ_QUERY = 'query UASCustomerWishlist($namespace: String!, $key: String!) { customer { id wishlist: metafield(namespace: $namespace, key: $key) { jsonValue compareDigest } } }';
  var WRITE_MUTATION = 'mutation UASSaveWishlist($metafields: [MetafieldsSetInput!]!) { metafieldsSet(metafields: $metafields) { metafields { namespace key jsonValue compareDigest } userErrors { field message code } } }';
  var AUTH_LIFETIME = 10 * 60 * 1000;
  var TOKEN_MARGIN = 60 * 1000;
  var REQUEST_TIMEOUT = 15000;
  var POPUP_STATE_PREFIX = 'uas_wishlist_popup_';
  var POPUP_MESSAGE = 'uas:wishlist:oauth-result';
  var POPUP_ACK = 'uas:wishlist:oauth-ack';
  var RELAY_LIFETIME = 15000;
  var config;
  var configError;
  var metadataPromise;
  var refreshPromise;
  var session;
  var queue = Promise.resolve();
  var snapshot = { items: [], customerId: null };
  var sessionKey;
  var transactionKey;
  var logoutKey;
  var logoutChannel;
  var authInProgress = false;
  var invalidated = false;
  var callbackRequest = null;
  var popupCallback = false;
  var popupReservation = null;
  var popupRelayCleanup = null;
  var authEpoch = 0;

  function error(code, message) {
    var failure = new Error(message);
    failure.code = code;
    return failure;
  }

  function friendly(failure) {
    return failure && failure.code ? failure : error('NETWORK_ERROR', 'Your wishlist could not be reached. Please try again.');
  }

  function httpsUrl(value, base) {
    var url;
    try { url = new URL(value, base); } catch (_) { throw error('CONFIGURATION_ERROR', 'Your wishlist is being set up. Please try again shortly.'); }
    if (url.protocol !== 'https:' || url.username || url.password) {
      throw error('CONFIGURATION_ERROR', 'Your wishlist is being set up. Please try again shortly.');
    }
    return url;
  }

  function cleanReturnPath(value) {
    var url;
    try { url = new URL(value || win.location.href, win.location.origin); } catch (_) { throw error('INVALID_RETURN_PATH', 'Please open your wishlist from this website.'); }
    if (url.origin !== win.location.origin || url.username || url.password || url.protocol !== 'https:') {
      throw error('INVALID_RETURN_PATH', 'Please open your wishlist from this website.');
    }
    ['code', 'state', 'error', 'error_description', 'error_uri', 'id_token', 'access_token', 'session_state'].forEach(function (key) { url.searchParams.delete(key); });
    return url.pathname + url.search + url.hash;
  }

  function customerNumber(value) {
    var text = value == null ? '' : String(value);
    var match = text.match(/^(?:gid:\/\/shopify\/Customer\/)?([1-9][0-9]*)$/);
    return match ? match[1] : null;
  }

  try {
    var input = suppliedConfig || {};
    var callback = httpsUrl(input.callbackUrl || '/pages/wishlist', win.location.origin);
    var discoveryBase = httpsUrl(input.discoveryBase || win.location.origin, win.location.origin);
    if (!input.clientId || typeof input.clientId !== 'string' || callback.origin !== win.location.origin || callback.hash) {
      throw error('CONFIGURATION_ERROR', 'Your wishlist is being set up. Please try again shortly.');
    }
    config = {
      clientId: input.clientId,
      discoveryBase: discoveryBase.origin,
      callbackUrl: callback.href,
      callbackPath: callback.pathname,
      customerId: customerNumber(input.customerId),
      namespace: input.namespace || 'uas_wishlist',
      key: input.key || 'items',
      previewThemeId: /^[0-9]+$/.test(String(input.previewThemeId || '')) ? String(input.previewThemeId) : null,
      maxItems: Number.isInteger(input.maxItems) && input.maxItems > 0 ? Math.min(input.maxItems, 200) : 200
    };
    sessionKey = 'uas:wishlist:session:' + config.clientId;
    transactionKey = 'uas:wishlist:oauth:' + config.clientId;
    logoutKey = 'uas:wishlist:logout:' + config.clientId;
  } catch (failure) { configError = friendly(failure); }

  // Run before the first promise or fetch so later page scripts never see OAuth credentials.
  try {
    var initialUrl = new URL(win.location.href);
    var intendedCallback = new URL((suppliedConfig || {}).callbackUrl || '/pages/wishlist', win.location.origin);
    if (intendedCallback.origin === win.location.origin && initialUrl.pathname === intendedCallback.pathname &&
        (initialUrl.searchParams.has('code') || initialUrl.searchParams.has('error'))) {
      callbackRequest = initialUrl;
      win.history.replaceState(win.history.state, '', cleanReturnPath(initialUrl.href));
      popupCallback = String(initialUrl.searchParams.get('state') || '').indexOf(POPUP_STATE_PREFIX) === 0;
      if (popupCallback && win.document.documentElement) {
        win.document.documentElement.style.visibility = 'hidden';
      }
    }
  } catch (_) { /* Initialization reports invalid configuration normally. */ }

  function storageRead(key) {
    try {
      var value = win.sessionStorage.getItem(key);
      return value ? JSON.parse(value) : null;
    } catch (_) { return null; }
  }

  function storageWrite(key, value) {
    try {
      win.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (_) {
      throw error('STORAGE_UNAVAILABLE', 'Please allow this site to save session data so you can sign in to your wishlist.');
    }
  }

  function storageRemove(key) {
    try { win.sessionStorage.removeItem(key); } catch (_) { /* In-memory state is still cleared. */ }
  }

  function copySnapshot(value) {
    return { items: value.items.map(function (item) { return Object.assign({}, item); }), customerId: value.customerId };
  }

  function emit(name, value) {
    win.document.dispatchEvent(new win.CustomEvent(name, { detail: copySnapshot(value) }));
  }

  function publish(value) {
    snapshot = copySnapshot(value);
    emit('uas:wishlist:change', snapshot);
    return copySnapshot(snapshot);
  }

  function clearSession() {
    session = null;
    if (sessionKey) storageRemove(sessionKey);
    publish({ items: [], customerId: null });
  }

  function logoutTime() {
    try { return Number(win.localStorage.getItem(logoutKey)) || 0; } catch (_) { return 0; }
  }

  function invalidateSession(notifyOtherTabs) {
    authEpoch += 1;
    invalidated = true;
    if (popupReservation) closeSignInPopup(popupReservation);
    if (popupRelayCleanup) popupRelayCleanup();
    clearSession();
    if (transactionKey) storageRemove(transactionKey);
    if (notifyOtherTabs !== false) {
      // Only an invalidation timestamp is shared. Saved parts and tokens never enter localStorage.
      try { win.localStorage.setItem(logoutKey, String(Date.now())); } catch (_) { /* BroadcastChannel can still notify open tabs. */ }
      if (logoutChannel) logoutChannel.postMessage({ type: 'logout' });
    }
  }

  if (config && typeof win.BroadcastChannel === 'function') {
    try {
      logoutChannel = new win.BroadcastChannel('uas:wishlist:' + config.clientId);
      logoutChannel.addEventListener('message', function (event) {
        if (event.data && event.data.type === 'logout') invalidateSession(false);
      });
    } catch (_) { /* The storage event remains available as a fallback. */ }
  }
  if (typeof win.addEventListener === 'function') {
    win.addEventListener('storage', function (event) {
      if (logoutKey && event.key === logoutKey && Number(event.newValue) > 0) invalidateSession(false);
    });
    win.addEventListener('pageshow', function (event) {
      if (event.persisted) {
        publish({ items: [], customerId: null });
        // A restored page contains an old Liquid account hint; re-render it before exposing a list.
        win.location.reload();
      }
    });
  }

  async function requestJson(url, options) {
    var controller = new win.AbortController();
    var timer = win.setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT);
    try {
      var response = await win.fetch(url, Object.assign({
        credentials: 'omit',
        cache: 'no-store',
        redirect: 'error',
        referrerPolicy: 'no-referrer',
        signal: controller.signal
      }, options || {}));
      var data;
      try { data = await response.json(); } catch (_) { throw error('INVALID_RESPONSE', 'Your wishlist could not be loaded. Please try again.'); }
      return { status: response.status, ok: response.ok, data: data };
    } catch (failure) {
      throw friendly(failure);
    } finally { win.clearTimeout(timer); }
  }

  async function discover() {
    if (configError) throw configError;
    if (!metadataPromise) {
      metadataPromise = Promise.all([
        requestJson(config.discoveryBase + '/.well-known/openid-configuration'),
        requestJson(config.discoveryBase + '/.well-known/customer-account-api')
      ]).then(function (responses) {
        if (!responses[0].ok || !responses[1].ok) throw error('CONFIGURATION_ERROR', 'Your wishlist connection is unavailable. Please try again shortly.');
        var oidc = responses[0].data;
        var api = responses[1].data;
        if (!oidc || !api || !oidc.authorization_endpoint || !oidc.token_endpoint || !oidc.issuer || !api.graphql_api) {
          throw error('CONFIGURATION_ERROR', 'Your wishlist connection is unavailable. Please try again shortly.');
        }
        return {
          authorization: httpsUrl(oidc.authorization_endpoint).href,
          token: httpsUrl(oidc.token_endpoint).href,
          issuer: httpsUrl(oidc.issuer).href.replace(/\/$/, ''),
          graphql: httpsUrl(api.graphql_api).href
        };
      }).catch(function (failure) { metadataPromise = null; throw failure; });
    }
    return metadataPromise;
  }

  function randomString() {
    if (!win.crypto || !win.crypto.subtle) throw error('UNSUPPORTED_BROWSER', 'Please use an up-to-date browser to sign in to your wishlist.');
    return toBase64Url(win.crypto.getRandomValues(new Uint8Array(32)));
  }

  function toBase64Url(bytes) {
    var binary = '';
    bytes.forEach(function (byte) { binary += String.fromCharCode(byte); });
    return win.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  async function challengeFor(verifier) {
    var digest = await win.crypto.subtle.digest('SHA-256', new win.TextEncoder().encode(verifier));
    return toBase64Url(new Uint8Array(digest));
  }

  function identityClaims(token, metadata, nonce, expectedSubject) {
    try {
      var parts = String(token).split('.');
      if (parts.length !== 3 || !parts[2]) throw new Error();
      var encoded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      var bytes = Uint8Array.from(win.atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=')), function (character) { return character.charCodeAt(0); });
      var claims = JSON.parse(new win.TextDecoder().decode(bytes));
      var audience = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
      var now = Date.now() / 1000;
      if (!audience.includes(config.clientId) || (audience.length > 1 && claims.azp !== config.clientId) ||
          String(claims.iss).replace(/\/$/, '') !== metadata.issuer || !claims.sub ||
          typeof claims.exp !== 'number' || claims.exp <= now ||
          (typeof claims.iat === 'number' && claims.iat > now + 60) ||
          (nonce && claims.nonce !== nonce) || (expectedSubject && claims.sub !== expectedSubject)) throw new Error();
      // The token is accepted only from the discovered HTTPS token endpoint, never from a URL or caller.
      return claims;
    } catch (_) { throw error('AUTH_VALIDATION_FAILED', 'We could not verify your sign-in. Please sign in again.'); }
  }

  function saveTokenResponse(data, metadata, transaction, previous) {
    if (!data || typeof data.access_token !== 'string' || !data.access_token ||
        !Number.isFinite(Number(data.expires_in)) || Number(data.expires_in) <= 0 ||
        (data.token_type && String(data.token_type).toLowerCase() !== 'bearer')) {
      throw error('AUTH_VALIDATION_FAILED', 'We could not verify your sign-in. Please sign in again.');
    }
    var claims;
    if (transaction || data.id_token) {
      claims = identityClaims(data.id_token, metadata, transaction ? transaction.nonce : null, previous && previous.subject);
    }
    session = {
      accessToken: data.access_token,
      refreshToken: data.refresh_token || (previous && previous.refreshToken) || null,
      expiresAt: Date.now() + Number(data.expires_in) * 1000,
      subject: claims ? claims.sub : previous.subject,
      customerId: previous ? previous.customerId : null,
      storefrontCustomerId: config.customerId,
      createdAt: previous ? previous.createdAt : Math.max(Date.now(), logoutTime() + 1)
    };
    storageWrite(sessionKey, session);
  }

  async function refreshAccess() {
    if (!session || !session.refreshToken || invalidated) {
      clearSession();
      throw error('AUTH_REQUIRED', 'Sign in to save parts and see your wishlist on any device.');
    }
    if (!refreshPromise) {
      var previous = session;
      refreshPromise = (async function () {
        var metadata = await discover();
        var body = new URLSearchParams({ grant_type: 'refresh_token', client_id: config.clientId, refresh_token: previous.refreshToken });
        var response = await requestJson(metadata.token, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
        if (!response.ok) {
          if (response.status === 400 || response.status === 401) {
            clearSession();
            throw error('AUTH_REQUIRED', 'Your sign-in has expired. Please sign in again to open your wishlist.');
          }
          throw error('NETWORK_ERROR', 'Your wishlist could not be reached. Please try again.');
        }
        if (invalidated || session !== previous) throw error('AUTH_REQUIRED', 'Please sign in again to open your wishlist.');
        saveTokenResponse(response.data, metadata, null, previous);
        return session.accessToken;
      })().finally(function () { refreshPromise = null; });
    }
    return refreshPromise;
  }

  async function accessToken(forceRefresh) {
    if (session && session.createdAt <= logoutTime()) invalidateSession(false);
    if (!session || invalidated) throw error('AUTH_REQUIRED', 'Sign in to save parts and see your wishlist on any device.');
    if (forceRefresh || session.expiresAt <= Date.now() + TOKEN_MARGIN) return refreshAccess();
    return session.accessToken;
  }

  async function graphql(query, variables) {
    var requestEpoch = authEpoch;
    var metadata = await discover();
    for (var attempt = 0; attempt < 2; attempt += 1) {
      var token = await accessToken(attempt > 0);
      var response = await requestJson(metadata.graphql, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ query: query, variables: variables })
      });
      var errors = response.data && response.data.errors;
      var unauthenticated = response.status === 401 || (Array.isArray(errors) && errors.some(function (entry) { return entry.extensions && entry.extensions.code === 'UNAUTHENTICATED'; }));
      if (unauthenticated && attempt === 0) continue;
      if (unauthenticated) {
        clearSession();
        throw error('AUTH_REQUIRED', 'Your sign-in has expired. Please sign in again to open your wishlist.');
      }
      if (response.status === 403 || (Array.isArray(errors) && errors.some(function (entry) { return entry.extensions && ['ACCESS_DENIED', 'FORBIDDEN'].includes(entry.extensions.code); }))) {
        throw error('PERMISSION_DENIED', 'Your wishlist connection needs attention. Please contact our support team.');
      }
      if (!response.ok || (Array.isArray(errors) && errors.length) || !response.data || !response.data.data) {
        throw error('API_ERROR', 'Your wishlist could not be updated. Please try again.');
      }
      if (invalidated || requestEpoch !== authEpoch) throw error('AUTH_REQUIRED', 'Please sign in again to open your wishlist.');
      return response.data.data;
    }
  }

  function validateVariant(value) {
    var variant = String(value == null ? '' : value).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
    if (!/^[1-9][0-9]{0,19}$/.test(variant)) throw error('INVALID_ITEM', 'This part could not be saved. Please reload its product page and try again.');
    return variant;
  }

  function validateItem(value, stored) {
    if (!value || typeof value.handle !== 'string' || !value.handle || value.handle.length > 255 || /[\\/\s?#<>\u0000-\u001f]/.test(value.handle)) {
      throw error(stored ? 'INVALID_DATA' : 'INVALID_ITEM', stored ? 'Your saved parts could not be read. Please contact our support team.' : 'This part could not be saved. Please reload its product page and try again.');
    }
    var item = { handle: value.handle, variantId: validateVariant(value.variantId), addedAt: value.addedAt };
    if (stored && (typeof item.addedAt !== 'string' || !Number.isFinite(Date.parse(item.addedAt)))) {
      throw error('INVALID_DATA', 'Your saved parts could not be read. Please contact our support team.');
    }
    if (!stored) item.addedAt = new Date().toISOString();
    return item;
  }

  function decodeItems(metafield) {
    if (!metafield) return [];
    var items = metafield.jsonValue;
    if (!Array.isArray(items) || items.length > 1000) throw error('INVALID_DATA', 'Your saved parts could not be read. Please contact our support team.');
    var variants = new Set();
    return items.map(function (item) {
      var clean = validateItem(item, true);
      if (variants.has(clean.variantId)) throw error('INVALID_DATA', 'Your saved parts could not be read. Please contact our support team.');
      variants.add(clean.variantId);
      return clean;
    });
  }

  async function fetchWishlist() {
    var result = await graphql(READ_QUERY, { namespace: config.namespace, key: config.key });
    var customer = result.customer;
    if (!customer || !customerNumber(customer.id)) {
      clearSession();
      throw error('AUTH_REQUIRED', 'Please sign in again to open your wishlist.');
    }
    var id = 'gid://shopify/Customer/' + customerNumber(customer.id);
    if ((session.customerId && session.customerId !== id) || (config.customerId && config.customerId !== customerNumber(id))) {
      invalidateSession();
      throw error('ACCOUNT_CHANGED', 'Your account has changed. Please sign in again to open the correct wishlist.');
    }
    session.customerId = id;
    if (config.customerId) session.storefrontCustomerId = config.customerId;
    storageWrite(sessionKey, session);
    if (customer.wishlist && typeof customer.wishlist.compareDigest !== 'string') throw error('INVALID_RESPONSE', 'Your wishlist could not be loaded. Please try again.');
    return { customerId: id, items: decodeItems(customer.wishlist), digest: customer.wishlist ? customer.wishlist.compareDigest : null };
  }

  async function readFresh() {
    var fresh = await fetchWishlist();
    return publish(fresh);
  }

  async function change(operation) {
    for (var attempt = 0; attempt < 4; attempt += 1) {
      var fresh = await fetchWishlist();
      var index = fresh.items.findIndex(function (item) { return item.variantId === operation.variantId; });
      var items = fresh.items.slice();
      if (operation.type === 'remove') {
        if (index < 0) return publish(fresh);
        items.splice(index, 1);
      } else {
        if (index >= 0 && items[index].handle === operation.item.handle) return publish(fresh);
        if (index >= 0) items[index] = Object.assign({}, items[index], { handle: operation.item.handle });
        else {
          if (items.length >= config.maxItems) throw error('WISHLIST_FULL', 'Your wishlist is full. Remove a saved part before adding another.');
          items.unshift(operation.item);
        }
      }
      var result = await graphql(WRITE_MUTATION, { metafields: [{
        ownerId: fresh.customerId,
        namespace: config.namespace,
        key: config.key,
        type: 'json',
        value: JSON.stringify(items),
        compareDigest: fresh.digest
      }] });
      var payload = result.metafieldsSet;
      if (!payload || !Array.isArray(payload.userErrors)) throw error('INVALID_RESPONSE', 'Your wishlist could not be updated. Please try again.');
      if (payload.userErrors.length) {
        if (payload.userErrors.every(function (entry) { return ['INVALID_COMPARE_DIGEST', 'STALE_OBJECT'].includes(entry.code); })) continue;
        if (payload.userErrors.some(function (entry) { return ['ACCESS_DENIED', 'FORBIDDEN', 'APP_NOT_AUTHORIZED', 'METAFIELD_ACCESS_DENIED'].includes(entry.code); })) {
          throw error('PERMISSION_DENIED', 'Your wishlist connection needs attention. Please contact our support team.');
        }
        throw error('SAVE_FAILED', 'This change was not saved. Please try again.');
      }
      var saved = Array.isArray(payload.metafields) && payload.metafields.find(function (field) { return field.namespace === config.namespace && field.key === config.key; });
      if (!saved || typeof saved.compareDigest !== 'string') throw error('INVALID_RESPONSE', 'We could not confirm your change. Please refresh your wishlist.');
      return publish({ items: decodeItems(saved), customerId: fresh.customerId });
    }
    throw error('CONFLICT', 'Your wishlist changed on another device. Please try your change again.');
  }

  function normalizeOperation(value) {
    if (!value) return null;
    if (value.type === 'add') {
      var item = validateItem(value.item, false);
      return { type: 'add', item: item, variantId: item.variantId };
    }
    if (value.type === 'remove') return { type: 'remove', variantId: validateVariant(value.variantId) };
    throw error('INVALID_ITEM', 'Please try saving this part again from its product page.');
  }

  function closePopupWindow(reservation) {
    if (!reservation) return;
    try { if (!reservation.window.closed) reservation.window.close(); } catch (_) { /* The browser may already have closed it. */ }
    if (popupReservation === reservation) popupReservation = null;
  }

  function openPopupChannel(state) {
    if (typeof win.BroadcastChannel !== 'function') return null;
    try { return new win.BroadcastChannel('uas:wishlist:oauth:' + config.clientId + ':' + state); }
    catch (_) { return null; }
  }

  function closeChannel(channel) {
    try { if (channel) channel.close(); } catch (_) { /* A disposed channel needs no further cleanup. */ }
  }

  function reserveSignInPopup() {
    if (configError) throw configError;
    if (authInProgress || (popupReservation && !popupReservation.window.closed)) {
      throw error('AUTH_IN_PROGRESS', 'Please finish signing in in the open window first.');
    }
    var popup;
    var popupName = 'uas-wishlist-signin-' + randomString();
    try {
      popup = win.open('about:blank', popupName, 'popup=yes,width=500,height=700,resizable=yes,scrollbars=yes');
    } catch (_) { /* A blocker is reported below without navigating this page. */ }
    if (!popup || popup.closed) throw error('POPUP_BLOCKED', 'Please allow the sign-in window for this site, then try saving this part again.');
    // A new same-origin window inherits a copy of the opener's sessionStorage.
    // The callback needs only the code/state relay, never an old token or verifier.
    try {
      popup.sessionStorage.removeItem(sessionKey);
      popup.sessionStorage.removeItem(transactionKey);
      if (popup.sessionStorage.getItem(sessionKey) !== null || popup.sessionStorage.getItem(transactionKey) !== null) throw new Error();
    } catch (_) {
      try { popup.close(); } catch (_) { /* Authentication must not start if isolation failed. */ }
      throw error('STORAGE_UNAVAILABLE', 'Please allow session data in the sign-in window, then try again.');
    }
    var reservation = { window: popup, cancel: null, authorizationStarted: false };
    popupReservation = reservation;
    try {
      popup.document.title = 'Sign in to your wishlist';
      popup.document.body.textContent = 'Connecting securely to your account…';
      popup.document.body.style.cssText = 'margin:0;padding:40px;font:16px/1.6 system-ui,sans-serif;color:#182233;background:#f5f9ff';
      popup.focus();
    } catch (_) { /* The waiting copy is optional; authentication still uses the reserved window. */ }
    return reservation;
  }

  function closeSignInPopup(reservation) {
    if (!reservation || reservation !== popupReservation) return;
    if (reservation.cancel) reservation.cancel(error('AUTH_CANCELLED', 'Sign-in was cancelled. Please try again when you are ready.'));
    closePopupWindow(reservation);
  }

  function awaitPopupResult(reservation, transaction) {
    return new Promise(function (resolve, reject) {
      var settled = false;
      var pollTimer;
      var deadlineTimer;
      var channel = openPopupChannel(transaction.state);
      function finish(failure, result) {
        if (settled) return;
        settled = true;
        win.removeEventListener('message', onMessage);
        win.clearTimeout(pollTimer);
        win.clearTimeout(deadlineTimer);
        closeChannel(channel);
        channel = null;
        reservation.cancel = null;
        if (failure) reject(failure);
        else resolve(result);
      }
      function acceptResult(data) {
        if (settled) return;
        if (!data || data.type !== POPUP_MESSAGE) return;
        if (data.state !== transaction.state ||
            (typeof data.code !== 'string' && typeof data.error !== 'string') ||
            (data.code && data.code.length > 4096)) {
          finish(error('AUTH_STATE_INVALID', 'We could not verify that sign-in window. Please try again.'));
          return;
        }
        var ack = { type: POPUP_ACK, state: transaction.state };
        try { reservation.window.postMessage(ack, win.location.origin); } catch (_) { /* COOP can sever the window reference. */ }
        try { if (channel) channel.postMessage(ack); } catch (_) { /* The window-message acknowledgement remains available. */ }
        var callback = new URL(transaction.redirectUri);
        callback.searchParams.set('state', data.state);
        if (data.error) callback.searchParams.set('error', data.error);
        else callback.searchParams.set('code', data.code);
        finish(null, callback);
      }
      function onMessage(event) {
        if (event.origin !== win.location.origin || event.source !== reservation.window) return;
        acceptResult(event.data);
      }
      function onChannelMessage(event) {
        if (event.origin !== win.location.origin) return;
        acceptResult(event.data);
      }
      function pollClosed() {
        if (settled) return;
        var closed;
        try { closed = reservation.window.closed; }
        catch (_) { closed = !(channel && reservation.authorizationStarted); }
        if (closed && !(channel && reservation.authorizationStarted)) {
          finish(error('AUTH_CANCELLED', 'The sign-in window closed before saving. Please try again when you are ready.'));
          return;
        }
        pollTimer = win.setTimeout(pollClosed, 500);
      }
      reservation.cancel = function (failure) { finish(failure); };
      win.addEventListener('message', onMessage);
      if (channel) {
        try { channel.addEventListener('message', onChannelMessage); }
        catch (_) { closeChannel(channel); channel = null; }
      }
      deadlineTimer = win.setTimeout(function () { finish(error('AUTH_TIMEOUT', 'Sign-in took too long. Please try again.')); }, AUTH_LIFETIME);
      pollTimer = win.setTimeout(pollClosed, 500);
    });
  }

  async function startSignIn(pendingOperation, options) {
    if (configError) throw configError;
    if (authInProgress) throw error('AUTH_IN_PROGRESS', 'Please finish signing in in the open window first.');
    var operation = normalizeOperation(pendingOperation);
    // Validate any old integration returnPath without ever using it to navigate the product page.
    var returnPath = cleanReturnPath(pendingOperation && pendingOperation.returnPath);
    var reservation = options && options.popup;
    if (reservation && (reservation !== popupReservation || reservation.window.closed)) {
      throw error('AUTH_CANCELLED', 'The sign-in window closed before saving. Please try again when you are ready.');
    }
    if (!reservation) reservation = reserveSignInPopup();
    authInProgress = true;
    authEpoch += 1;
    if (session) clearSession();
    invalidated = false;
    var resultPromise;
    try {
      var current = new URL(win.location.href);
      var target = new URL(returnPath, win.location.origin);
      var theme = win.Shopify && win.Shopify.theme;
      var draftTheme = theme && ['unpublished', 'development'].includes(theme.role) ? String(theme.id) : null;
      var preview = current.searchParams.get('preview_theme_id') || draftTheme || config.previewThemeId;
      if (preview && /^[0-9]+$/.test(preview) && !target.searchParams.has('preview_theme_id')) target.searchParams.set('preview_theme_id', preview);
      var verifier = randomString();
      var transaction = {
        state: POPUP_STATE_PREFIX + randomString(), nonce: randomString(), verifier: verifier,
        createdAt: Date.now(), redirectUri: config.callbackUrl,
        returnPath: cleanReturnPath(target.href), operation: operation,
        previewThemeId: preview && /^[0-9]+$/.test(preview) ? preview : null,
        mode: 'popup'
      };
      storageWrite(transactionKey, transaction);
      resultPromise = awaitPopupResult(reservation, transaction);
      // Attach immediately, since cancellation can happen while discovery is pending.
      resultPromise.catch(function () {});
      var metadata = await discover();
      var authorization = new URL(metadata.authorization);
      authorization.searchParams.set('client_id', config.clientId);
      authorization.searchParams.set('response_type', 'code');
      authorization.searchParams.set('redirect_uri', config.callbackUrl);
      authorization.searchParams.set('scope', 'openid email customer-account-api:full');
      authorization.searchParams.set('state', transaction.state);
      authorization.searchParams.set('nonce', transaction.nonce);
      authorization.searchParams.set('code_challenge', await challengeFor(verifier));
      authorization.searchParams.set('code_challenge_method', 'S256');
      if (reservation.window.closed || invalidated) throw error('AUTH_CANCELLED', 'Sign-in was cancelled. Please try again when you are ready.');
      reservation.authorizationStarted = true;
      reservation.window.location.replace(authorization.href);
      var callback = await resultPromise;
      closePopupWindow(reservation);
      return await handleCallback(callback, true);
    } catch (failure) {
      throw failure;
    } finally {
      if (reservation.cancel) reservation.cancel(error('AUTH_CANCELLED', 'Sign-in was cancelled. Please try again when you are ready.'));
      storageRemove(transactionKey);
      closePopupWindow(reservation);
      authInProgress = false;
    }
  }

  function relayPopupCallback(url) {
    var state = url.searchParams.get('state');
    var code = url.searchParams.get('code');
    var authError = url.searchParams.get('error');
    var opener;
    try { opener = win.opener; } catch (_) { opener = null; }
    function showStatus(message) {
      function render() {
        if (win.document.body) {
          win.document.body.textContent = message;
          win.document.body.style.cssText = 'margin:0;padding:40px;font:16px/1.6 system-ui,sans-serif;color:#182233;background:#f5f9ff';
        }
        if (win.document.documentElement) win.document.documentElement.style.visibility = 'visible';
      }
      if (win.document.body) render();
      else win.document.addEventListener('DOMContentLoaded', render, { once: true });
    }
    if (!state || state.length > 200 || url.searchParams.getAll('state').length !== 1 ||
        (code && (code.length > 4096 || url.searchParams.getAll('code').length !== 1)) || (!code && !authError)) {
      showStatus('This sign-in window could not reconnect. Close it and try saving your part again on the product page.');
      throw error('AUTH_POPUP_UNAVAILABLE', 'The sign-in window could not reconnect. Please try again from the product page.');
    }
    var channel = openPopupChannel(state);
    var stopped = false;
    var retryTimer;
    var deadlineTimer;
    function cleanup() {
      if (stopped) return;
      stopped = true;
      win.clearTimeout(retryTimer);
      win.clearTimeout(deadlineTimer);
      win.removeEventListener('message', onWindowAck);
      win.removeEventListener('pagehide', cleanup);
      closeChannel(channel);
      channel = null;
      if (popupRelayCleanup === cleanup) popupRelayCleanup = null;
    }
    function acceptAck(data) {
      if (stopped || !data || data.type !== POPUP_ACK || data.state !== state) return;
      cleanup();
      try { win.close(); } catch (_) { /* The neutral status remains if automatic close is unavailable. */ }
    }
    function onWindowAck(event) {
      if (event.origin === win.location.origin && event.source === opener) acceptAck(event.data);
    }
    function onChannelAck(event) {
      if (event.origin === win.location.origin) acceptAck(event.data);
    }
    if (channel) {
      try { channel.addEventListener('message', onChannelAck); }
      catch (_) { closeChannel(channel); channel = null; }
    }
    var connectedOpener = false;
    try { connectedOpener = !!opener && !opener.closed; } catch (_) { /* The same-origin channel survives opener isolation. */ }
    if (!connectedOpener && !channel) {
      showStatus('This sign-in window could not reconnect. Close it and try saving your part again on the product page.');
      throw error('AUTH_POPUP_UNAVAILABLE', 'The sign-in window could not reconnect. Please try again from the product page.');
    }
    win.addEventListener('message', onWindowAck);
    win.addEventListener('pagehide', cleanup, { once: true });
    popupRelayCleanup = cleanup;
    showStatus('Completing sign-in. You can return to your product page.');
    var result = { type: POPUP_MESSAGE, state: state, code: code || undefined, error: authError || undefined };
    function send() {
      if (stopped) return;
      try { if (connectedOpener) opener.postMessage(result, win.location.origin); } catch (_) { /* Relay through the same-origin channel instead. */ }
      try { if (channel) channel.postMessage(result); } catch (_) { /* A later bounded retry can recover delivery. */ }
      if (!stopped) retryTimer = win.setTimeout(send, 500);
    }
    deadlineTimer = win.setTimeout(function () {
      cleanup();
      showStatus('This sign-in window could not reconnect. Close it and try saving your part again on the product page.');
    }, RELAY_LIFETIME);
    send();
    // The opener alone owns the verifier, performs the token exchange, and writes the wishlist.
    return copySnapshot(snapshot);
  }

  async function handleCallback(url, stayOnPage) {
    var callbackEpoch = authEpoch;
    var transaction = storageRead(transactionKey);
    var state = url.searchParams.get('state');
    storageRemove(transactionKey);
    if (!transaction || !state || state !== transaction.state ||
        !transaction.verifier || !transaction.nonce ||
        !Number.isFinite(transaction.createdAt) || Date.now() - transaction.createdAt > AUTH_LIFETIME || transaction.createdAt > Date.now() + 60000 ||
        transaction.redirectUri !== config.callbackUrl) {
      throw error('AUTH_STATE_INVALID', 'Your sign-in link has expired. Please sign in to your wishlist again.');
    }
    var returnPath = cleanReturnPath(transaction.returnPath);
    var target = new URL(returnPath, win.location.origin);
    if (transaction.previewThemeId && /^[0-9]+$/.test(transaction.previewThemeId)) target.searchParams.set('preview_theme_id', transaction.previewThemeId);
    returnPath = cleanReturnPath(target.href);
    if (url.searchParams.has('error')) {
      throw error('AUTH_REQUIRED', 'Sign-in was not completed. Please sign in again to open your wishlist.');
    }
    var code = url.searchParams.get('code');
    if (!code || url.searchParams.getAll('code').length !== 1 || url.searchParams.getAll('state').length !== 1) throw error('AUTH_STATE_INVALID', 'Your sign-in link has expired. Please sign in to your wishlist again.');
    var metadata = await discover();
    var body = new URLSearchParams({ grant_type: 'authorization_code', client_id: config.clientId, redirect_uri: config.callbackUrl, code: code, code_verifier: transaction.verifier });
    var response = await requestJson(metadata.token, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
    if (!response.ok) throw error('AUTH_FAILED', 'We could not complete your sign-in. Please try again.');
    if (invalidated || callbackEpoch !== authEpoch) throw error('AUTH_CANCELLED', 'Sign-in was cancelled. Please try again when you are ready.');
    saveTokenResponse(response.data, metadata, transaction, null);
    var operation = normalizeOperation(transaction.operation);
    var result = operation ? await change(operation) : await readFresh();
    if (!stayOnPage && returnPath !== cleanReturnPath(win.location.href)) win.location.replace(returnPath);
    return result;
  }

  async function initialize() {
    if (configError) throw configError;
    if (callbackRequest) return popupCallback ? relayPopupCallback(callbackRequest) : handleCallback(callbackRequest);
    session = storageRead(sessionKey);
    if (session && (typeof session.accessToken !== 'string' || !Number.isFinite(session.expiresAt) || !session.subject)) clearSession();
    if (session && ((config.customerId && session.customerId && config.customerId !== customerNumber(session.customerId)) ||
        (session.storefrontCustomerId && !config.customerId))) invalidateSession();
    if (session && session.createdAt <= logoutTime()) invalidateSession(false);
    if (session) return readFresh();
    return copySnapshot(snapshot);
  }

  var ready = Promise.resolve().then(initialize).then(function (result) {
    emit('uas:wishlist:ready', result);
    return copySnapshot(result);
  }).catch(function (failure) { throw friendly(failure); });
  // Consumers can await the rejection; suppress an unhandled rejection before UI modules attach.
  ready.catch(function () {});

  function enqueue(work) {
    var task = queue.catch(function () {}).then(async function () {
      try { await ready; } catch (failure) {
        if (configError || !session) throw failure;
        // A transient initial read failure can be retried without reloading the page.
      }
      return work();
    }).catch(function (failure) { throw friendly(failure); });
    queue = task;
    return task;
  }

  win.document.addEventListener('click', function (event) {
    var target = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!target) return;
    var url;
    try { url = new URL(target.href, win.location.origin); } catch (_) { return; }
    if (url.origin === win.location.origin && /\/(?:customer_authentication|account)\/logout\/?$/.test(url.pathname)) {
      invalidateSession();
    }
  }, true);

  return {
    ready: ready,
    read: function () { return enqueue(readFresh); },
    add: function (item) {
      return enqueue(function () {
        var clean = validateItem(item, false);
        return change({ type: 'add', item: clean, variantId: clean.variantId });
      });
    },
    remove: function (variantId) {
      return enqueue(function () { return change({ type: 'remove', variantId: validateVariant(variantId) }); });
    },
    signIn: function (pendingOperation, options) { return startSignIn(pendingOperation, options).catch(function (failure) { throw friendly(failure); }); },
    reserveSignInPopup: reserveSignInPopup,
    closeSignInPopup: closeSignInPopup,
    isAuthenticated: function () { return !!(session && session.accessToken && !invalidated && session.createdAt > logoutTime()); },
    invalidateSession: function () { invalidateSession(); },
    has: function (variantId) {
      var id = String(variantId).replace(/^gid:\/\/shopify\/ProductVariant\//, '');
      return snapshot.items.some(function (item) { return item.variantId === id; });
    }
  };
});
